import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import useEffectiveRole from '../lib/useEffectiveRole'
import PhotoUpload from './PhotoUpload'

// ANU-132 — Field Updates (F-UP) section.
// Two channels:
//   FIELD    — any authenticated event participant; subject to 3-level kill switch
//   OFFICIAL — IGA / co_gm / gm / platform admins; always on, cannot be disabled

const OFFICIAL_ROLES = ['m55_admin', 'm55_staff', 'm55_support', 'gm', 'co_gm', 'iga']

const D = {
  display: { fontFamily: "'Stardos Stencil', 'Special Elite', serif" },
  body:    { fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  ink3:    { color: '#7a7058' },
}

function getGeoAsync() {
  return new Promise(resolve => {
    if (!navigator?.geolocation) return resolve(null)
    navigator.geolocation.getCurrentPosition(
      p => resolve({ geo_lat: p.coords.latitude, geo_lng: p.coords.longitude, geo_accuracy_m: Math.round(p.coords.accuracy) }),
      () => resolve(null),
      { timeout: 3000 }
    )
  })
}

export function FUPSection({ parentType, parentId, eventId, sectionNum }) {
  const { user } = useAuth()
  const { effectiveRole } = useEffectiveRole(eventId)
  const [activeTab, setActiveTab] = useState('official')
  const [entries, setEntries] = useState(null)
  const [authorMap, setAuthorMap] = useState({})
  const [signedUrls, setSignedUrls] = useState({})
  const [killSwitches, setKillSwitches] = useState({ orgEnabled: true, eventEnabled: true, runtimeEnabled: true })
  const [draft, setDraft] = useState('')
  const [photoPath, setPhotoPath] = useState(null)
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null)
  const [photoUploading, setPhotoUploading] = useState(false)
  const [updateProfile, setUpdateProfile] = useState(false)
  const [posting, setPosting] = useState(false)
  const [postError, setPostError] = useState(null)
  const [lightboxUrl, setLightboxUrl] = useState(null)

  const fetchEntries = useCallback(async () => {
    const { data } = await supabase
      .from('dossier_entries_view')
      .select('*')
      .eq('parent_type', parentType)
      .eq('parent_id', parentId)
      .eq('entry_type', 'f_up')
      .order('created_at', { ascending: true })

    const rows = data || []
    setEntries(rows)

    const ids = [...new Set(rows.map(e => e.author_id).filter(Boolean))]
    if (ids.length) {
      const { data: users } = await supabase
        .from('users').select('id, callsign, display_name').in('id', ids)
      setAuthorMap(Object.fromEntries((users || []).map(u => [u.id, u])))
    }

    const urls = {}
    for (const e of rows) {
      if (e.photo_storage_path) {
        const { data: s } = await supabase.storage
          .from('event-media').createSignedUrl(e.photo_storage_path, 3600)
        if (s?.signedUrl) urls[e.id] = s.signedUrl
      }
    }
    setSignedUrls(urls)
  }, [parentType, parentId])

  useEffect(() => {
    if (!eventId || !parentId) return

    const fetchKillSwitches = async () => {
      const { data: ev } = await supabase
        .from('events')
        .select('field_intel_enabled, field_intel_runtime_enabled, org_id')
        .eq('id', eventId)
        .single()
      if (!ev) return
      const { data: org } = await supabase
        .from('organizations')
        .select('field_intel_enabled')
        .eq('id', ev.org_id)
        .single()
      setKillSwitches({
        orgEnabled:     org?.field_intel_enabled ?? true,
        eventEnabled:   ev.field_intel_enabled ?? true,
        runtimeEnabled: ev.field_intel_runtime_enabled ?? true,
      })
    }

    fetchKillSwitches()
    fetchEntries()

    const channel = supabase.channel(`fup-${parentType}-${parentId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'dossier_entries',
        filter: `parent_id=eq.${parentId}`,
      }, () => fetchEntries())
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'dossier_entries',
        filter: `parent_id=eq.${parentId}`,
      }, () => fetchEntries())
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [eventId, parentId, fetchEntries])

  const fieldEnabled = killSwitches.orgEnabled && killSwitches.eventEnabled && killSwitches.runtimeEnabled
  const canPostField    = fieldEnabled && effectiveRole && effectiveRole !== 'none'
  const canPostOfficial = OFFICIAL_ROLES.includes(effectiveRole)

  const handlePost = async () => {
    if (!draft.trim()) return
    setPosting(true)
    setPostError(null)
    const tab = activeTab

    const geo = await getGeoAsync().catch(() => null)

    const { error } = await supabase.from('dossier_entries').insert({
      event_id:           eventId,
      parent_type:        parentType,
      parent_id:          parentId,
      entry_type:         'f_up',
      entry_subtype:      tab,
      author_id:          user.id,
      body:               draft.trim(),
      photo_storage_path: photoPath || null,
      update_profile:     tab === 'official' && updateProfile && !!photoPath,
      ...(geo || {}),
    })

    setPosting(false)
    if (error) { setPostError('Post failed — ' + error.message); return }
    setDraft('')
    setPhotoPath(null)
    setPhotoPreviewUrl(null)
    setUpdateProfile(false)
    fetchEntries()
  }

  if (entries === null) return null

  const tabEntries = entries.filter(e => e.entry_subtype === activeTab)
  const uploadFolder = `${eventId}/${parentType}/${parentId}/fup`
  const canPost = activeTab === 'field' ? canPostField : canPostOfficial

  return (
    <>
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
        >
          <img src={lightboxUrl} alt="Intel photo" style={{ maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain', boxShadow: '0 0 40px rgba(0,0,0,.8)' }} />
        </div>
      )}
      <div style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#4a4636', borderBottom: '1px solid #cbb98c', paddingBottom: 3, marginBottom: 8, marginTop: 14 }}>
        § {String(sectionNum).padStart(2, '0')} — FIELD UPDATES
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', marginBottom: 10, borderBottom: '1px solid rgba(120,100,60,.25)' }}>
        {['official', 'field'].map(tab => (
          <button key={tab} type="button" onClick={() => setActiveTab(tab)}
            style={{
              ...D.display, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '5px 14px', background: 'none', border: 'none', cursor: 'pointer',
              color: activeTab === tab ? '#1f1d18' : '#7a7058',
              borderBottom: `2px solid ${activeTab === tab ? '#a82a1f' : 'transparent'}`,
              marginBottom: -1,
            }}>
            {tab === 'field' ? 'FIELD' : 'OFFICIAL'}
          </button>
        ))}
      </div>

      {/* Timeline entries */}
      {tabEntries.length === 0 && (
        <div style={{ ...D.body, fontSize: 11, color: '#7a7058', fontStyle: 'italic', marginBottom: 10 }}>
          No {activeTab} intel on file.
        </div>
      )}
      {tabEntries.map(entry => {
        const author = authorMap[entry.author_id]
        const signedUrl = signedUrls[entry.id] || null
        return (
          <div key={entry.id} style={{ background: '#e8dec3', border: '1px solid rgba(120,100,60,.3)', padding: '10px 12px', marginBottom: 8, display: 'flex', gap: 10 }}>
            {signedUrl ? (
              <div
                onClick={() => setLightboxUrl(signedUrl)}
                style={{ flexShrink: 0, width: 64, height: 80, border: '1px solid #7a7058', overflow: 'hidden', cursor: 'zoom-in' }}>
                <img src={signedUrl} alt="Intel photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ) : entry.photo_is_redacted ? (
              <div style={{ flexShrink: 0, width: 64, height: 80, background: '#1a1a1a', border: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <span style={{ ...D.display, fontSize: 6, color: '#444', letterSpacing: 2, transform: 'rotate(-30deg)', position: 'absolute' }}>CLASSIFIED</span>
              </div>
            ) : null}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...D.body, fontSize: 12, color: '#1f1d18', lineHeight: 1.5, marginBottom: 4, whiteSpace: 'pre-wrap' }}>
                {entry.body}
              </div>
              <div style={{ fontSize: 9, color: '#7a7058', letterSpacing: '0.06em' }}>
                {author?.callsign || author?.display_name || 'UNKNOWN'}{' '}·{' '}
                {new Date(entry.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        )
      })}

      {/* State messages for blocked viewers */}
      {activeTab === 'field' && !fieldEnabled && (
        <div style={{ ...D.display, fontSize: 9, color: '#a82a1f', letterSpacing: '0.12em', textTransform: 'uppercase', textAlign: 'center', padding: '8px 0', border: '1px solid rgba(168,42,31,.3)', background: 'rgba(168,42,31,.05)', marginTop: 4 }}>
          FIELD INTEL DISABLED
        </div>
      )}
      {activeTab === 'official' && !canPostOfficial && (
        <div style={{ ...D.display, fontSize: 9, color: '#7a7058', letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: 'center', padding: '8px 0' }}>
          OFFICIAL CHANNEL — IGA / COMMAND ONLY
        </div>
      )}

      {/* Post form */}
      {canPost && (
        <div style={{ background: '#e0d4ac', border: '1px solid rgba(120,100,60,.35)', padding: '10px 12px', marginTop: 8 }}>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={activeTab === 'official' ? 'Official intel update...' : 'Field intel update...'}
            rows={3}
            style={{ width: '100%', background: '#d8c898', color: '#1f1d18', border: '1px solid rgba(120,100,60,.4)', padding: '6px 8px', fontFamily: "'Special Elite', monospace", fontSize: 12, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
          />
          <div style={{ marginTop: 6 }}>
            <PhotoUpload
              folder={uploadFolder}
              label="Attach Photo (optional)"
              existingUrl={photoPreviewUrl}
              onUploadStart={() => setPhotoUploading(true)}
              onUpload={(signedUrl, path) => { setPhotoPath(path); setPhotoPreviewUrl(signedUrl); setPhotoUploading(false) }}
            />
          </div>
          {photoUploading && <div style={{ ...D.body, fontSize: 10, color: '#7a7058', marginTop: 4 }}>Uploading photo...</div>}
          {activeTab === 'official' && photoPath && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={updateProfile}
                onChange={e => setUpdateProfile(e.target.checked)}
                style={{ accentColor: '#a82a1f', width: 14, height: 14, cursor: 'pointer' }}
              />
              <span style={{ ...D.body, fontSize: 10, color: '#4a4636' }}>Set as dossier profile photo</span>
            </label>
          )}
          {postError && <div style={{ ...D.body, fontSize: 10, color: '#a82a1f', marginTop: 4 }}>{postError}</div>}
          <button type="button" onClick={handlePost}
            disabled={posting || photoUploading || !draft.trim()}
            style={{
              ...D.display, marginTop: 8, padding: '5px 14px',
              background: '#1f1d18', color: '#d8c898', border: '1px solid #7a7058',
              fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
              cursor: posting || photoUploading || !draft.trim() ? 'not-allowed' : 'pointer',
              opacity: posting || photoUploading || !draft.trim() ? 0.5 : 1,
            }}>
            {posting ? 'FILING...' : 'FILE INTEL'}
          </button>
        </div>
      )}
    </>
  )
}
